/**
 * usePolls / usePollDetail - Poll list & detail state management.
 * `createUserId` is enriched with a nickname via useUserProfiles so callers
 * can render "由 XXX 發起" without a separate lookup.
 * Subscribes to POLL_* realtime events (T037) — a no-op unless called from
 * within a TripRealtimeProvider.
 */

import { useCallback, useEffect, useState } from 'react';
import { pollService, PollApiError } from '../services/pollService';
import { useUserProfiles } from './useUserProfiles';
import { useTripRealtimeSubscribe } from '../contexts/TripRealtimeContext';
import { REALTIME_EVENTS } from '../utils/realtimeEvents';
import type { CreatePollRequest, Poll, PollSummary } from '../types/poll';

export interface PollSummaryWithCreator extends PollSummary {
  creatorNickname: string;
}

export function usePolls(tripId: string | undefined) {
  const [polls, setPolls] = useState<PollSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { profiles } = useUserProfiles(polls.map((p) => p.createUserId));

  const pollsWithCreator: PollSummaryWithCreator[] = polls.map((poll) => ({
    ...poll,
    creatorNickname: profiles.get(poll.createUserId)?.nickname ?? '...',
  }));

  const refetch = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await pollService.getPolls(tripId);
      setPolls(list);
    } catch (err) {
      setError(err instanceof PollApiError ? err.message : '取得投票列表失敗');
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useTripRealtimeSubscribe(REALTIME_EVENTS.POLL_CREATED, refetch);
  useTripRealtimeSubscribe(REALTIME_EVENTS.POLL_VOTED, refetch);
  useTripRealtimeSubscribe(REALTIME_EVENTS.POLL_VOTE_REVOKED, refetch);
  useTripRealtimeSubscribe(REALTIME_EVENTS.POLL_CLOSED, refetch);
  useTripRealtimeSubscribe(REALTIME_EVENTS.RECONNECTED, refetch);

  const createPoll = useCallback(
    async (input: CreatePollRequest) => {
      if (!tripId) return;
      const pollId = await pollService.createPoll(tripId, input);
      await refetch();
      return pollId;
    },
    [tripId, refetch]
  );

  return { polls: pollsWithCreator, loading, error, refetch, createPoll };
}

export interface PollWithCreator extends Poll {
  creatorNickname: string;
}

export function usePollDetail(tripId: string | undefined, pollId: string | undefined) {
  const [poll, setPoll] = useState<Poll | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { profiles } = useUserProfiles(poll ? [poll.createUserId] : []);

  const pollWithCreator: PollWithCreator | null = poll
    ? { ...poll, creatorNickname: profiles.get(poll.createUserId)?.nickname ?? '...' }
    : null;

  const refetch = useCallback(async () => {
    if (!tripId || !pollId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await pollService.getPollDetail(tripId, pollId);
      setPoll(detail);
    } catch (err) {
      setError(err instanceof PollApiError ? err.message : '取得投票詳情失敗');
    } finally {
      setLoading(false);
    }
  }, [tripId, pollId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const refetchIfCurrentPoll = useCallback(
    (data: { pollId: number | string }) => {
      if (String(data.pollId) === String(pollId)) refetch();
    },
    [pollId, refetch]
  );

  useTripRealtimeSubscribe(REALTIME_EVENTS.POLL_VOTED, refetchIfCurrentPoll);
  useTripRealtimeSubscribe(REALTIME_EVENTS.POLL_VOTE_REVOKED, refetchIfCurrentPoll);
  useTripRealtimeSubscribe(REALTIME_EVENTS.POLL_CLOSED, refetchIfCurrentPoll);
  useTripRealtimeSubscribe(REALTIME_EVENTS.RECONNECTED, refetch);

  const castVote = useCallback(
    async (optionId: string) => {
      if (!tripId || !pollId) return;
      await pollService.vote(tripId, pollId, optionId);
      await refetch();
    },
    [tripId, pollId, refetch]
  );

  const revoke = useCallback(
    async (optionId: string) => {
      if (!tripId || !pollId) return;
      await pollService.revokeVote(tripId, pollId, optionId);
      await refetch();
    },
    [tripId, pollId, refetch]
  );

  const close = useCallback(async () => {
    if (!tripId || !pollId) return;
    await pollService.closePoll(tripId, pollId);
    await refetch();
  }, [tripId, pollId, refetch]);

  return {
    poll: pollWithCreator,
    loading,
    error,
    refetch,
    vote: castVote,
    revokeVote: revoke,
    closePoll: close,
  };
}
